/**
 * lenz-coupon-blocker.js
 *
 * Trabaja en conjunto con la Shopify Function `block-card-with-coupon`
 * (app `precios` / `lenz-payment-rules`).
 *
 * Por qué existe:
 *   La API moderna de Payment Customization Function NO expone los
 *   discount codes aplicados al cart. Por eso, desde el theme detectamos
 *   cuándo se aplica un cupón "bloqueado" (default: 5ADT) y seteamos un
 *   cart attribute `coupon_blocks_card` que la function lee del input.
 *
 * Cómo funciona:
 *   1. Boot: fetch /cart.js → mira cart.discount_codes → si hay algún
 *      código bloqueado y applicable=true, setea attributes.coupon_blocks_card="true"
 *      vía /cart/update.js. Sino, lo deja vacío.
 *   2. Suscribe a `cart:change` (que el theme dispatcha en cada add/remove/qty
 *      change) → revisa el cart del event.detail y vuelve a sincronizar.
 *   3. Suscribe a `cart:refresh` → re-fetch /cart.js y sincroniza.
 *
 * Para extender las reglas cupón→attribute, editar RULES.
 *
 * Limitaciones:
 *   - Si el cliente aplica el cupón directamente en checkout (no en cart/drawer),
 *     este script no lo ve porque corre en storefront, no en checkout.
 *     La function en Shopify ya cubre eso indirectamente: el flag se actualiza
 *     en la próxima vista del cart, y el customer todavía no llegó al pago.
 *   - Si Shopify cambia el shape de /cart.js, romper grácilmente sin afectar
 *     el resto del theme.
 */

(function () {
  "use strict";

  // ─── Configuración ───────────────────────────────────────────────────────

  // Reglas cupón → cart attribute que lee la Shopify Function
  // block-card-with-coupon. Cada regla: si alguno de sus codes está
  // aplicado al cart con applicable=true, su attr se setea en "true".
  // Comparación case-insensitive.
  //   - coupon_blocks_card   → 5ADT: oculta tarjetas/wallets (solo transferencia)
  //   - coupon_powerpay_only → POWER80: oculta todo EXCEPTO PowerPay
  var RULES = [
    { codes: ["5ADT"], attr: "coupon_blocks_card" },
    { codes: ["POWER80"], attr: "coupon_powerpay_only" },
  ];

  // Logging opcional. Activar con `localStorage.setItem('LENZ_DEBUG_BLOCKER','1')`.
  var DEBUG = false;
  try {
    DEBUG = window.localStorage && window.localStorage.getItem("LENZ_DEBUG_BLOCKER") === "1";
  } catch (e) {
    /* localStorage puede fallar en privado; seguimos */
  }
  function log() {
    if (!DEBUG) return;
    try {
      var args = Array.prototype.slice.call(arguments);
      args.unshift("[lenz-coupon-blocker]");
      console.log.apply(console, args);
    } catch (e) {
      /* noop */
    }
  }

  // ─── Estado ──────────────────────────────────────────────────────────────

  // Últimos valores seteados (JSON de attrs), para evitar requests duplicados.
  // null = nunca seteado en esta sesión (forzar primer chequeo).
  var lastValue = null;

  // ─── Helpers ─────────────────────────────────────────────────────────────

  /**
   * @param {Array} discountCodes - cart.discount_codes de /cart.js
   * @param {Array} codes - códigos de la regla
   * @returns {boolean}
   */
  function hasBlockedCoupon(discountCodes, codes) {
    if (!Array.isArray(discountCodes) || discountCodes.length === 0) return false;
    var blockedLower = codes.map(function (c) { return String(c).toLowerCase(); });
    for (var i = 0; i < discountCodes.length; i++) {
      var dc = discountCodes[i];
      if (!dc || !dc.applicable) continue;
      var code = (dc.code || "").toLowerCase();
      if (code && blockedLower.indexOf(code) !== -1) return true;
    }
    return false;
  }

  /**
   * Calcula los valores deseados de todos los attributes según el cart.
   *
   * @param {Array} discountCodes
   * @returns {Object} attrs {attr: "true"|""}
   */
  function desiredAttributes(discountCodes) {
    var attrs = {};
    for (var i = 0; i < RULES.length; i++) {
      attrs[RULES[i].attr] = hasBlockedCoupon(discountCodes, RULES[i].codes) ? "true" : "";
    }
    return attrs;
  }

  /**
   * Setea (o borra) los cart attributes. Si los valores coinciden con los
   * últimos seteados, no hace nada para no spamear /cart/update.js.
   *
   * @param {Object} attrs
   */
  function syncAttribute(attrs) {
    var value = JSON.stringify(attrs);
    if (lastValue === value) {
      log("attributes ya están en", value, "— skip");
      return;
    }
    lastValue = value;
    log("setting attributes", value);

    var body = { attributes: attrs };

    // No bloqueamos el flujo principal. Si falla, el chequeo periódico /
    // próximo cart:change reintenta.
    try {
      fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify(body),
        keepalive: true
      }).catch(function (err) {
        log("update.js failed:", err);
        // Reintentar permitir corrección en el siguiente evento.
        lastValue = null;
      });
    } catch (e) {
      log("fetch threw:", e);
      lastValue = null;
    }
  }

  /**
   * Toma un cart object (de /cart.js o de event.detail.cart) y sincroniza
   * el attribute si hace falta.
   */
  function checkCart(cart) {
    if (!cart) return;

    var desired = desiredAttributes(cart.discount_codes);

    // Si el cart ya tiene todos los attributes con el valor correcto, no
    // reseteamos.
    var inSync = true;
    for (var key in desired) {
      var currentAttr = cart.attributes && cart.attributes[key];
      var currentNorm = typeof currentAttr === "string" ? currentAttr : "";
      if (currentNorm !== desired[key]) { inSync = false; break; }
    }
    if (inSync) {
      lastValue = JSON.stringify(desired);
      log("cart already in sync");
      return;
    }

    syncAttribute(desired);
  }

  /**
   * Fetch del cart actual y sync.
   */
  function fetchAndCheck() {
    try {
      fetch("/cart.js", { headers: { "Accept": "application/json" } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) { if (cart) checkCart(cart); })
        .catch(function (err) { log("/cart.js fetch failed:", err); });
    } catch (e) {
      log("fetch /cart.js threw:", e);
    }
  }

  // ─── Wire-up ─────────────────────────────────────────────────────────────

  // 1) Boot: chequeo inicial
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", fetchAndCheck);
  } else {
    fetchAndCheck();
  }

  // 2) cart:change → el theme nos pasa el cart en event.detail.cart
  document.addEventListener("cart:change", function (event) {
    var cart = event && event.detail && event.detail.cart;
    if (cart) {
      checkCart(cart);
    } else {
      // Algunos dispatchers (lenz custom) podrían no incluir cart. Re-fetch.
      fetchAndCheck();
    }
  });

  // 3) cart:refresh → no trae detail; hay que re-fetch
  document.addEventListener("cart:refresh", fetchAndCheck);

  log("ready, rules:", RULES);
})();
